import React from 'react';
import { Button, MixedLineBarChartProps } from '@amzn/awsui-components-core';
import { useChartSummaryContext } from './ChartSummaryContext';
import { fetchChartSummary } from 'src/api/app-sync-events-service';
import { useEventsApiContext } from 'src/components/context/EventsApiContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { v4 as uuidv4 } from 'uuid';
import { ChartSummaryPanel } from './ChartSummaryPanel';
import { processFiltersForAISummary } from 'src/utilities/CommonUtilities';

// Union type for different chart data types used in the application
export type ChartDataItem =
  | MixedLineBarChartProps.BarDataSeries<string | number | Date>
  | MixedLineBarChartProps.LineDataSeries<string | number | Date>
  | { [key: string]: any }; // Using 'any' here because pie chart data and custom chart types have varying structures that don't conform to a single interface.
// This provides flexibility for different chart implementations.

interface ChartSummaryButtonProps {
  pageType: string;
  chartTitle: string;
  chartData: ChartDataItem[];
  chartType?: string;
  chartDomain?: string;
  filters?: string;
  originalFilters?: string;
  onOpenSplitPanel?: (isSplitPanelOpen: boolean, header: React.ReactNode, body: React.ReactNode) => void;
}

export const ChartSummaryButton: React.FC<ChartSummaryButtonProps> = ({
  pageType,
  chartTitle,
  chartData,
  chartType,
  chartDomain = '',
  filters = '',
  originalFilters = '',
  onOpenSplitPanel
}) => {
  const { addSummary, setIsLoading, setIsSplitPanelOpen } = useChartSummaryContext();
  const eventsApiContext = useEventsApiContext();
  const { Alias } = useAuth();

  const isChartDataEmpty = () => {
    return !chartData || chartData.length === 0 || (Array.isArray(chartData) && chartData.every((item) => !item || Object.keys(item).length === 0));
  };

  const fetchSummary = async () => {
    try {
      setIsLoading(true);

      // Check if chart data is empty before making API call
      if (isChartDataEmpty()) {
        addSummary(chartTitle, 'No summary generated due to lack of data.');
        return;
      }

      const sessionId = uuidv4();
      const chartInfo = chartTitle + `, ${chartDomain}`;

      if (!eventsApiContext) {
        console.error('eventsApiContext is not available. Make sure the component is wrapped in EventsApiContextProvider');
        addSummary(chartTitle, 'Unable to load summary: missing context');
        setIsLoading(false);
        return;
      }

      const currentFilters = processFiltersForAISummary(filters, originalFilters);
      const { message, summaryId } = await fetchChartSummary(
        sessionId,
        pageType,
        chartType || 'metrics',
        chartInfo || 'Chart',
        chartData,
        eventsApiContext,
        Alias,
        currentFilters
      );

      addSummary(chartTitle, message, summaryId);
    } catch (error) {
      console.error('Error fetching chart summary:', error);
      addSummary(chartTitle, 'Unable to load summary. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormAction = () => {
    // If onOpenSplitPanel is provided (for Risk Remediator), use it
    if (onOpenSplitPanel) {
      onOpenSplitPanel(true, 'Chart Summary', React.createElement(ChartSummaryPanel));
      fetchSummary();
    } else {
      // Fallback to original behavior for other pages
      setIsSplitPanelOpen(true);
      fetchSummary();
    }
    return false; // Prevent default form submission
  };

  return (
    <Button variant="normal" onClick={handleFormAction} iconName="gen-ai" ariaLabel="AI-generated chart summary" iconAlt="AI">
      Summarize
    </Button>
  );
};
